import { useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { Button } from '../atoms';
import { PropertyFilters, PropertyListTable } from '../organism';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { PropertyListCardsTable } from '../organism/PropertyListCardsTable';
import { OrderDirectionOption, PageSizeOption, PropertyFilterInput, useGetPropertiesQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';

const CategoryCardsIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7.24 2H5.34C3.15 2 2 3.15 2 5.33V7.23C2 9.41 3.15 10.56 5.33 10.56H7.23C9.41 10.56 10.56 9.41 10.56 7.23V5.33C10.57 3.15 9.42 2 7.24 2Z"
      fill="currentColor"
      fillOpacity="0.3"
    />
    <path
      d="M18.67 2H16.77C14.59 2 13.44 3.15 13.44 5.33V7.23C13.44 9.41 14.59 10.56 16.77 10.56H18.67C20.85 10.56 22 9.41 22 7.23V5.33C22 3.15 20.85 2 18.67 2Z"
      fill="currentColor"
      fillOpacity="0.3"
    />
    <path
      d="M18.67 13.4301H16.77C14.59 13.4301 13.44 14.5801 13.44 16.7601V18.6601C13.44 20.8401 14.59 21.9901 16.77 21.9901H18.67C20.85 21.9901 22 20.8401 22 18.6601V16.7601C22 14.5801 20.85 13.4301 18.67 13.4301Z"
      fill="currentColor"
      fillOpacity="0.3"
    />
    <path
      d="M7.24 13.4301H5.34C3.15 13.4301 2 14.5801 2 16.7601V18.6601C2 20.8501 3.15 22.0001 5.33 22.0001H7.23C9.41 22.0001 10.56 20.8501 10.56 18.6701V16.7701C10.57 14.5801 9.42 13.4301 7.24 13.4301Z"
      fill="currentColor"
      fillOpacity="0.3"
    />
  </svg>
);

const CategoryListIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M5.34778 2H4.13389C2.73472 2 2 2.73472 2 4.1275V5.34139C2 6.73416 2.73472 7.46889 4.1275 7.46889H5.34139C6.73416 7.46889 7.46889 6.73416 7.46889 5.34139V4.1275C7.47527 2.73472 6.74055 2 5.34778 2Z"
      fill="currentColor"
    />
    <path
      d="M20.6735 3.02441H11.0256C10.1506 3.02441 9.69116 3.48387 9.69116 4.35483V5.11392C9.69116 5.98489 10.1506 6.44434 11.0216 6.44434H20.6696C21.5405 6.44434 22 5.98489 22 5.11392V4.35483C22.004 3.48387 21.5445 3.02441 20.6735 3.02441Z"
      fill="currentColor"
    />
    <path
      d="M20.6735 10.3334H11.0256C10.1506 10.3334 9.69116 10.7928 9.69116 11.6638V12.4229C9.69116 13.2938 10.1506 13.7533 11.0216 13.7533H20.6696C21.5405 13.7533 22 13.2938 22 12.4229V11.6638C22.004 10.7928 21.5445 10.3334 20.6735 10.3334Z"
      fill="currentColor"
    />
    <path
      d="M20.6735 18.1111H11.0256C10.1506 18.1111 9.69116 18.5705 9.69116 19.4415V20.2006C9.69116 21.0716 10.1506 21.531 11.0216 21.531H20.6696C21.5405 21.531 22 21.0716 22 20.2006V19.4415C22.004 18.5705 21.5445 18.1111 20.6735 18.1111Z"
      fill="currentColor"
    />
    <path
      d="M5.34778 9.22217H4.13389C2.73472 9.22217 2 9.95689 2 11.3497V12.5636C2 13.9627 2.73472 14.6974 4.1275 14.6974H5.34139C6.73416 14.6974 7.46889 13.9627 7.46889 12.5699V11.3561C7.47527 9.95689 6.74055 9.22217 5.34778 9.22217Z"
      fill="currentColor"
    />
    <path
      d="M5.34778 17H4.13389C2.73472 17 2 17.7347 2 19.1275V20.3414C2 21.7406 2.73472 22.4753 4.1275 22.4753H5.34139C6.73416 22.4753 7.46889 21.7406 7.46889 20.3478V19.1339C7.47527 17.7347 6.74055 17 5.34778 17Z"
      fill="currentColor"
    />
  </svg>
);

const textsData = {
  en: {
    title: 'Estate Properties',
    buttonText: '+ Add Property'
  },
  es: {
    title: 'Propiedades Inmobiliarias',
    buttonText: '+ Agregar Propiedad'
  }
}

const initialPropertyFilters: PropertyFilterInput = {
  type: null,
  occupancyStatus: null,
  countryId: null,
  stateId: null,
  cityId: null,
  balcony: null,
  elevator: null,
  patio: null,
  kitchen: null,
  commonBathroom: null,
  commonAirConditioner: null,
  commonFan: null,
  orderDirection: OrderDirectionOption.Desc,
  search: null,
  pageSize: PageSizeOption.TwentyFive,
  page: null,
};

const ButtonContainer = styled(Col)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`

export const EstatePropertyPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('properties')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(savedState.filters as PropertyFilterInput)
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('properties')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<PropertyFilterInput>(() => {
    const savedState = getListState('properties')
    return (savedState?.filters as PropertyFilterInput) ?? initialPropertyFilters
  });
  const [isCardsViewMode, setCardsViewMode] = useState(false)

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('properties', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: PropertyFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const { data } = useGetPropertiesQuery({
    variables: {
      input: { ...filters, page: currentPage.toString() },
    },
    fetchPolicy: 'network-only',
  });

  const currentDataCount = data?.properties?.items?.length || 0;
  const totalPages = data?.properties?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  return (
    <Container fluid>
      <Row>
        <Col>
            <h2>{texts.title}</h2>
        </Col>
        <ButtonContainer xs="content">
          <Button
            onClick={() => setCardsViewMode((prev) => !prev)}
            secondary
            aria-label={isCardsViewMode ? 'Cambiar a vista de lista' : 'Cambiar a vista de tarjetas'}
          >
            {isCardsViewMode ? <CategoryListIcon /> : <CategoryCardsIcon />}
          </Button>
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.PROPERTY}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </ButtonContainer>
      </Row>
      <Row>
        <Col><PropertyFilters filters={filters} onFilterChange={handleFilterChange} /></Col>
      </Row>
      <Row>
        <Col>
          {
            isCardsViewMode
            ? <PropertyListCardsTable filters={{ ...filters, page: currentPage.toString() }} />
            : <PropertyListTable filters={{ ...filters, page: currentPage.toString() }} />
          }
          
        </Col>
      </Row>
      {showPagination && (
        <Row>
          <Col>
            <Pagination
              currentPage={currentPage}
              onPageChange={handlePageChange}
              currentDataCount={currentDataCount}
              pageSize={filters.pageSize || null}
              totalPages={totalPages}
            />
          </Col>
        </Row>
      )}
    </Container>
  )
}
